/**
 * Email Tracking deep links: the outbox health views link into the email
 * table pre-filtered, and the table reads those filters back from its URL.
 */

export const EMAIL_TRACKING_PATH = '/admin/email-tracking';

// The filters GET /api/admin/email-tracking accepts that a link may carry.
const LINK_FILTER_KEYS = [
	'status',
	'type',
	'search',
	'older_than_hours',
	'date_from',
	'date_to',
	'sort_by',
	'sort_order',
];

// The Status select's options. A link's status outside them is dropped: the
// select could not show it, so the list would be filtered with nothing on
// screen saying why.
export const STATUS_OPTIONS = [
	{ value: 'all', label: 'All Statuses' },
	{ value: 'pending', label: 'Pending' },
	{ value: 'sent', label: 'Sent' },
	{ value: 'failed', label: 'Failed' },
];

const isOfferedStatus = (value) => STATUS_OPTIONS.some((option) => option.value === value);

// The outbox_rows_stale rule's types (backend OutboxHealth::STALE_ROW_TYPES):
// reminders are left out, since rows no loop reads stay live for months.
export const STALE_ROW_TYPES = [
	'alert',
	'notification',
	'expiring1',
	'expiring7',
	'expiring14',
];

// Oldest first, so the rows that waited longest lead the table.
export const OLDEST_FIRST = { sort_by: 'created_at', sort_order: 'asc' };

/**
 * The Email Tracking URL for a set of filters, in the order given; empty
 * filters are left out.
 */
export const emailTrackingUrl = (filters) => {
	const params = new URLSearchParams();
	Object.entries(filters).forEach(([key, value]) => {
		if (value !== null && value !== undefined && value !== '') {
			params.append(key, String(value));
		}
	});
	const query = params.toString();
	return query ? `${EMAIL_TRACKING_PATH}?${query}` : EMAIL_TRACKING_PATH;
};

// The backend rejects an older_than_hours below 1 with a 422.
const isPositiveWholeNumber = (value) => /^[1-9][0-9]*$/.test(value);

/**
 * The Email Tracking filters a URL query string carries; anything else in it
 * (a Queue Monitor tab, say) is ignored, and so is an older_than_hours that
 * is not a positive whole number or a status the Status select does not offer.
 */
export const filtersFromSearch = (search) => {
	const params = new URLSearchParams(search || '');
	const filters = {};
	LINK_FILTER_KEYS.forEach((key) => {
		if (!params.has(key)) {
			return;
		}
		const value = params.get(key);
		if (key === 'older_than_hours' && !isPositiveWholeNumber(value)) {
			return;
		}
		if (key === 'status' && !isOfferedStatus(value)) {
			return;
		}
		filters[key] = value;
	});
	return filters;
};

/**
 * The URL query string with the Email Tracking filter keys taken out (other
 * keys stay), so the URL stops carrying a deep link's filters once the user
 * changes them.
 */
export const searchWithoutLinkFilters = (search) => {
	const params = new URLSearchParams(search || '');
	LINK_FILTER_KEYS.forEach((key) => params.delete(key));
	const query = params.toString();
	return query ? `?${query}` : '';
};
